import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { forwardRef, useRef, useState, useEffect, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { dsI18n, getCommonClassNameDefault } from '@skatteetaten/ds-core-utils';
import { CancelSVGpath, MenuSVGpath, LogOutSVGpath, LockOutlineSVGpath } from '@skatteetaten/ds-icons';
import { TopBannerButton } from '../TopBannerButton/index.esm.js';
import { TopBannerLangPicker } from '../TopBannerLangPicker/index.esm.js';
import { TopBannerLogo } from '../TopBannerLogo/index.esm.js';
import { TopBannerSkipLink } from '../TopBannerSkipLink/index.esm.js';
import { TopBannerUserButton } from '../TopBannerUserButton/index.esm.js';
import '../TopBannerLangPickerButton/index.esm.js';
import './styles.css';


var styles = {
    "topContainer": "TopBannerExternal-module_topContainer__2I7q6",
    "contentContainer": "TopBannerExternal-module_contentContainer__-O6UD",
    "content": "TopBannerExternal-module_content__-4USV",
    "separator": "TopBannerExternal-module_separator__2g-uV",
    "separatorLeft": "TopBannerExternal-module_separatorLeft__xvbme",
    "separatorRight": "TopBannerExternal-module_separatorRight__FQGU-",
    "containerForMainMenu": "TopBannerExternal-module_containerForMainMenu__KQada",
    "mainMenu": "TopBannerExternal-module_mainMenu__jATae",
    "columns": "TopBannerExternal-module_columns__23viR",
    "columnsThree": "TopBannerExternal-module_columnsThree__ZiZU6",
    "columnsTwo": "TopBannerExternal-module_columnsTwo__Wx0aX",
    "column": "TopBannerExternal-module_column__AYYsq"
};

const TopBannerExternal = /*#__PURE__*/ forwardRef(({ id, className = getCommonClassNameDefault(), lang, 'data-testid': dataTestId, firstColumn, defaultLocale, logo, secondColumn, skipLink, thirdColumn, user, additionalLanguages, showSami, children, onLanguageClick, onLogInClick, onLogOutClick, onUserClick }, ref)=>{
    const { t } = useTranslation('ds_layout', {
        i18n: dsI18n
    });
    const innerRef = useRef(null);
    const menuRef = useRef(null);
    const menuButtonRef = useRef(null);
    const languagePickerRef = useRef(null);
    const languagePickerButtonRef = useRef(null);
    const [openMenu, setOpenMenu] = useState('None');
    const isMenuOpen = openMenu === 'MainMenu';
    const statusFlagRef = useRef({
        focusCaptured: false,
        mouseUpCaptured: false,
        mouseDownCaptured: false
    });
    useEffect(()=>{
        if (openMenu === 'None') {
            return;
        }
        const currentButtonRef = openMenu === 'Lang' ? languagePickerButtonRef : menuButtonRef;
        const handleEscape = (e)=>{
            if (e.key === 'Escape') {
                setOpenMenu('None');
                currentButtonRef?.current?.focus();
            }
        };
        /**
       * Setter fokusflagg i en ref i stedet for å bruke contains(event.target) slik at detektering
       * også fungerer i tilfelle med createPortal
       */ const handleFocusOutside = (_)=>{
            if (!statusFlagRef.current.focusCaptured && isMenuOpen) {
                setOpenMenu('None');
            }
            statusFlagRef.current.focusCaptured = false;
        };
        const handleClickOutside = (event)=>{
            if (event.target === menuButtonRef.current) {
                return;
            }
            const clickCaptured = statusFlagRef.current.mouseDownCaptured || statusFlagRef.current.mouseUpCaptured;
            if (!clickCaptured && isMenuOpen) {
                setOpenMenu('None');
            }
            statusFlagRef.current.mouseUpCaptured = false;
            statusFlagRef.current.mouseDownCaptured = false;
        };
        if (openMenu === 'MainMenu') {
            document.addEventListener('mouseup', handleClickOutside);
            document.addEventListener('focusin', handleFocusOutside);
        }
        document.addEventListener('keydown', handleEscape);
        return ()=>{
            document.removeEventListener('mouseup', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
            document.removeEventListener('focusin', handleFocusOutside);
        };
    }, [
        openMenu,
        isMenuOpen
    ]);
    useImperativeHandle(ref, ()=>({
            ...innerRef,
            openMenu: ()=>{
                setOpenMenu('MainMenu');
            },
            closeMenu: ()=>{
                setOpenMenu('None');
            }
        }));
    const handleMenuClick = ()=>{
        setOpenMenu((openMenu)=>openMenu === 'MainMenu' ? 'None' : 'MainMenu');
    };
    const showMenu = firstColumn || secondColumn || thirdColumn;
    const threeColumnsClassName = thirdColumn ? styles.columnsThree : '';
    const twoColumnsClassName = secondColumn ? styles.columnsTwo : '';
    return /*#__PURE__*/ jsxs("header", {
        ref: innerRef,
        id: id,
        lang: lang,
        "data-testid": dataTestId,
        className: className,
        children: [
            /*#__PURE__*/ jsx(TopBannerSkipLink, {
                target: skipLink?.target,
                shadowRootNode: skipLink?.shadowRootNode,
                children: skipLink?.text
            }),
            /*#__PURE__*/ jsxs("div", {
                className: styles.containerForMainMenu,
                children: [
                    /*#__PURE__*/ jsxs("div", {
                        className: styles.topContainer,
                        children: [
                            /*#__PURE__*/ jsx(TopBannerLogo, {
                                ...logo
                            }),
                            /*#__PURE__*/ jsxs("div", {
                                className: styles.contentContainer,
                                children: [
                                    children,
                                    /*#__PURE__*/ jsxs("div", {
                                        className: styles.content,
                                        children: [
                                            showMenu && /*#__PURE__*/ jsxs(Fragment, {
                                                children: [
                                                    /*#__PURE__*/ jsx(TopBannerButton, {
                                                        ref: menuButtonRef,
                                                        svgPath: openMenu === 'MainMenu' ? CancelSVGpath : MenuSVGpath,
                                                        ariaExpanded: isMenuOpen,
                                                        onClick: handleMenuClick,
                                                        onFocus: ()=>{
                                                            statusFlagRef.current.focusCaptured = isMenuOpen;
                                                        },
                                                        children: t('topbannerbutton.Menu')
                                                    }),
                                                    isMenuOpen && /*#__PURE__*/ jsx("div", {
                                                        ref: menuRef,
                                                        className: styles.mainMenu,
                                                        onFocus: ()=>{
                                                            statusFlagRef.current.focusCaptured = isMenuOpen;
                                                        },
                                                        onMouseUp: ()=>{
                                                            statusFlagRef.current.mouseUpCaptured = isMenuOpen;
                                                        },
                                                        onMouseDown: ()=>{
                                                            statusFlagRef.current.mouseDownCaptured = isMenuOpen;
                                                        },
                                                        children: /*#__PURE__*/ jsxs("nav", {
                                                            "aria-label": t('topbanner.NavAriaLabel'),
                                                            className: `${styles.columns} ${threeColumnsClassName} ${twoColumnsClassName}`.trim(),
                                                            children: [
                                                                /*#__PURE__*/ jsx("div", {
                                                                    className: styles.column,
                                                                    children: firstColumn
                                                                }),
                                                                secondColumn && /*#__PURE__*/ jsx("div", {
                                                                    className: styles.column,
                                                                    children: secondColumn
                                                                }),
                                                                thirdColumn && /*#__PURE__*/ jsx("div", {
                                                                    className: styles.column,
                                                                    children: thirdColumn
                                                                })
                                                            ]
                                                        })
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ jsx(TopBannerLangPicker, {
                                                ref: languagePickerRef,
                                                defaultLocale: defaultLocale,
                                                showSami: showSami,
                                                openMenu: openMenu,
                                                setOpenMenu: setOpenMenu,
                                                menuButtonRef: languagePickerButtonRef,
                                                additionalLanguages: additionalLanguages,
                                                onLanguageClick: onLanguageClick
                                            }),
                                            onLogOutClick && user && /*#__PURE__*/ jsxs(Fragment, {
                                                children: [
                                                    /*#__PURE__*/ jsx(TopBannerUserButton, {
                                                        user: user,
                                                        onClick: onUserClick
                                                    }),
                                                    /*#__PURE__*/ jsx(TopBannerButton, {
                                                        svgPath: LogOutSVGpath,
                                                        variant: 'outline',
                                                        onClick: onLogOutClick,
                                                        children: t('topbannerbutton.Logout')
                                                    })
                                                ]
                                            }),
                                            onLogInClick && !user && /*#__PURE__*/ jsx(TopBannerButton, {
                                                svgPath: LockOutlineSVGpath,
                                                variant: 'filled',
                                                onClick: onLogInClick,
                                                children: t('topbannerbutton.Login')
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ jsxs("div", {
                        className: styles.separator,
                        children: [
                            /*#__PURE__*/ jsx("div", {
                                className: styles.separatorRight
                            }),
                            /*#__PURE__*/ jsx("div", {
                                className: styles.separatorLeft
                            })
                        ]
                    })
                ]
            })
        ]
    });
});
TopBannerExternal.displayName = 'TopBannerExternal';

export { TopBannerExternal };
